<template>
  <OverlayPanel
    v-bind="$attrs"
    :id="id"
    :ref="id"
    :show-close-icon="true"
    unstyled
    :pt="getComponentClasses"
    @keydown.esc="onEsc($event)"
    @hide="onHide()"
  >
    <slot />
    <!-- https://v3.primevue.org/overlaypanel/#api.overlaypanel.slots -->
    <template #closeicon>
      <Icon class="p-overlaypanel-close-icon pi pi-times" size="s" :icon="csgClose" />
    </template>
  </OverlayPanel>
</template>

<script>
import OverlayPanel from 'primevue/overlaypanel';
import { csgClose } from '@csgSvgIcons/icons';
import generateRandomId from '../../utils/randomNumber';
import Icon from '../icon';

export default {
  name: 'Toggletip',
  components: {
    OverlayPanel,
    Icon,
  },
  props: {
    position: {
      type: String,
      default: 'right',
    },
    id: {
      type: [String, Number],
      default: () => generateRandomId(),
    },
    width: {
      type: Number,
      default: 284,
      required: false,
    },
  },
  data() {
    return {
      leftVal: 0,
      topVal: 0,
      showOverlay: false,
      currentPosition: this.position,
      targetEl: null,
      csgClose,
    };
  },
  computed: {
    getComponentClasses() {
      return {
        root: {
          class: `p-overlaypanel p-component ${this.currentPosition}-toggletip-arrow`,
        },
        content: {
          class: 'p-overlaypanel-content',
          style: { width: `${this.width}px` },
        },
        closeButton: 'p-overlaypanel-close p-link',
        closeIcon: 'p-overlaypanel-close',
      };
    },
  },
  mounted() {
    import('./toggletip.scss');
  },
  methods: {
    toggleOverlay() {
      this.showOverlay = !this.showOverlay;
    },
    async toggle(event) {
      this.$refs[this.id].toggle(event);
      this.toggleOverlay();
      if (this.showOverlay) {
        await this.$nextTick(() => {
          this.repositionToggletip(event.currentTarget || event.target);
          this.targetEl = event.currentTarget || event.target;
        });
        this.showOverlay = null;
      }
    },
    onEsc() {
      this.$refs[this.id].hide();
    },
    onHide() {
      this.targetEl?.focus();
    },
    getWindowScrollTop() {
      const doc = document.documentElement;
      return (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    },
    getWindowScrollLeft() {
      const doc = document.documentElement;
      return (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    },
    getOffset(el) {
      const rect = el.getBoundingClientRect();
      return {
        left: rect.left + this.getWindowScrollLeft(),
        top: rect.top + this.getWindowScrollTop(),
      };
    },
    getOuterWidth(el) {
      return el.offsetWidth;
    },
    getOuterHeight(el) {
      return el.offsetHeight;
    },
    getToggletipElement() {
      return document.getElementById(this.id);
    },
    isOutOfBounds() {
      const el = this.getToggletipElement();
      const rect = el.getBoundingClientRect();
      return (
        rect.left < 0
        || rect.top < 0
        || rect.right > window.innerWidth
        || rect.bottom > window.innerHeight
      );
    },
    repositionToggletip(targetEl) {
      this.currentPosition = this.position;
      this.positionToggletip(targetEl, this.currentPosition);

      let attemptPositions;
      if (this.position === 'top') {
        attemptPositions = ['bottom', 'right', 'left'];
      } else if (this.position === 'right') {
        attemptPositions = ['left', 'top', 'bottom'];
      } else {
        attemptPositions = ['right', 'top', 'bottom', 'left'].filter((p) => p !== this.position);
      }

      for (let i = 0; i < attemptPositions.length; i++) {
        if (!this.isOutOfBounds()) break;
        this.positionToggletip(targetEl, attemptPositions[i]);
      }
    },
    positionToggletip(targetEl, position) {
      const hostOffset = this.getOffset(targetEl);
      const toggletipElement = this.getToggletipElement();
      const elWidth = this.getOuterWidth(targetEl);
      const elHeight = this.getOuterHeight(targetEl);
      const tooltipWidth = this.getOuterWidth(toggletipElement);
      const tooltipHeight = this.getOuterHeight(toggletipElement);

      switch (position) {
      case 'top':
        this.leftVal = hostOffset.left + (elWidth - tooltipWidth) / 2;
        this.topVal = hostOffset.top - tooltipHeight;
        break;
      case 'right':
        this.leftVal = hostOffset.left + elWidth;
        this.topVal = hostOffset.top + (elHeight - tooltipHeight) / 2;
        break;
      case 'bottom':
        this.leftVal = hostOffset.left + (elWidth - tooltipWidth) / 2;
        this.topVal = hostOffset.top + elHeight;
        break;
      case 'left':
        this.leftVal = hostOffset.left - tooltipWidth;
        this.topVal = hostOffset.top + (elHeight - tooltipHeight) / 2;
        break;
      default:
        break;
      }

      toggletipElement.style.left = `${this.leftVal}px`;
      toggletipElement.style.top = `${this.topVal}px`;
      this.currentPosition = position;
    },
  },
};
</script>
